import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { trackSignupAbandonFeedback } from '@/lib/utils/analytics';

const STORAGE_KEY = 'sf_signup_exit_responded';
const ARM_DELAY_MS = 5000;

type Reason = 'justBrowsing' | 'wantedInfoFirst' | 'tooComplicated' | 'other';

const OPTIONS: { value: Reason; label: string }[] = [
  { value: 'justBrowsing', label: "Just browsing, not ready yet" },
  { value: 'wantedInfoFirst', label: 'Wanted more info before signing up' },
  { value: 'tooComplicated', label: 'Signup looked complicated' },
  { value: 'other', label: 'Something else' },
];

/**
 * TSF-117: /signup-creator has the biggest known drop-off (signup_completed stuck at 3
 * vs 61+ visits) and zero qualitative signal on why. Fires on exit-intent (mouse leaving
 * toward the browser chrome) rather than a timer, since a timer can't tell "about to leave"
 * from "still reading" the way a fast upward mouse movement can.
 *
 * mouseleave is desktop-only -- 69% of Sport Fund's traffic is Mobile (PostHog, 30d,
 * measured run 2026-09-02) and can never fire it, which is a more likely explanation for
 * zero signup_abandon_feedback events than raw traffic volume. The Page Visibility API is
 * the standard mobile analog: it fires when a visitor backgrounds the tab/app (switches
 * apps, locks the screen) rather than requiring a cursor.
 */
export default function SignupExitIntentPrompt() {
  const [visible, setVisible] = useState(false);
  const [answered, setAnswered] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.localStorage.getItem(STORAGE_KEY)) return;

    let armed = false;
    const armTimer = window.setTimeout(() => {
      armed = true;
    }, ARM_DELAY_MS);

    const onMouseLeave = (e: MouseEvent) => {
      if (!armed) return;
      if (e.clientY > 0) return;
      setVisible(true);
    };

    const onVisibilityChange = () => {
      if (!armed) return;
      if (document.visibilityState !== 'hidden') return;
      setVisible(true);
    };

    document.addEventListener('mouseleave', onMouseLeave);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.clearTimeout(armTimer);
      document.removeEventListener('mouseleave', onMouseLeave);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);

  const respond = (reason: Reason | 'dismissed') => {
    if (answered) return;
    trackSignupAbandonFeedback(reason);
    window.localStorage.setItem(STORAGE_KEY, '1');
    setAnswered(true);
    window.setTimeout(() => setVisible(false), 1600);
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/30 p-4">
      <div className="relative w-full max-w-sm rounded-2xl border border-slate-200 bg-white p-5 shadow-2xl shadow-black/10 animate-in fade-in zoom-in-95">
        <button
          type="button"
          onClick={() => respond('dismissed')}
          aria-label="Close"
          className="absolute right-3 top-3 text-slate-400 hover:text-slate-600"
        >
          <X className="h-4 w-4" />
        </button>
        {answered ? (
          <p className="pr-4 text-sm font-medium text-slate-700">Thanks — that helps us fix the signup flow.</p>
        ) : (
          <>
            <p className="pr-4 text-sm font-semibold text-slate-900">Leaving already? Quick one — what stopped you from signing up?</p>
            <div className="mt-3 flex flex-col gap-2">
              {OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => respond(opt.value)}
                  className="rounded-full border border-slate-200 px-4 py-2 text-left text-sm font-medium text-slate-700 hover:border-blue-300 hover:bg-primary/10 hover:text-blue-800 transition-colors"
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
